import { Field } from "@base-ui/react/field";
import { cva } from "class-variance-authority";
import { type KeyboardEvent, useId, useState } from "react";
import { cn } from "@/lib/utils";
import { FieldLabel } from "../field-label/field-label";

const fieldContainerVariants = cva(
  [
    "relative",
    "flex",
    "w-full",
    "min-h-[132px]",
    "overflow-hidden",
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "border-input-default-outline",
    "bg-input-default-background",
    "p-(--spacing-16)",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "[&:not([data-disabled=true]):not(:focus-within):not(:has([readonly]))]:active:border-input-pressed-outline",
    "[&:not([data-disabled=true]):focus-within:not(:has([readonly]))]:border-input-active-outline",
  ],
  {
    variants: {
      hasError: {
        true: ["border-input-error-outline"],
        false: [],
      },
    },
    defaultVariants: {
      hasError: false,
    },
  },
);

const textareaStyles = cva([
  "flex-1",
  "min-w-0",
  "w-full",
  "min-h-[98px]",
  "border-0",
  "outline-none",
  "bg-transparent",
  "resize-y",
  "overflow-auto",
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "text-text-default",
  "placeholder:text-input-text-placeholder",
  "disabled:cursor-not-allowed",
  "read-only:cursor-default",
  "read-only:resize-none",
]);

function getTextareaValue(
  value: TextareaProps["value"] | TextareaProps["defaultValue"],
) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.join(",");
  }

  return "";
}

export interface TextareaProps
  extends Omit<React.ComponentPropsWithoutRef<"textarea">, "className"> {
  /** Label text rendered above the field. Omit to hide. */
  label?: string;
  /**
   * Error message. Presence sets `aria-invalid` and replaces the character
   * counter in the footer. Ignored when `readOnly` is true.
   */
  error?: string;
  /** Tooltip content displayed as an info icon in the label row. */
  tooltip?: string;
}

/**
 * Multi-line text input with label, error state, and optional character
 * counter. Renders a native `<textarea>` with vertical resize. Footer row is
 * reserved in the DOM to prevent layout shift on validation.
 *
 * Use `readOnly` for non-editable display (removes footer and resize handle,
 * sets `aria-readonly`). Use `maxLength` to show a `current/max` counter.
 *
 * @example
 * <Textarea label="Description" placeholder="Enter description..." />
 * <Textarea label="Notes" maxLength={1000} />
 * <Textarea label="Bio" error="Bio is required" />
 * <Textarea label="Summary" readOnly value="Lorem ipsum..." />
 */
function Textarea({
  id,
  label,
  error,
  tooltip,
  required,
  disabled,
  readOnly,
  maxLength,
  value,
  defaultValue,
  placeholder,
  tabIndex,
  onChange,
  onKeyDown,
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  ...props
}: TextareaProps) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const [internalValue, setInternalValue] = useState(() =>
    getTextareaValue(defaultValue),
  );
  const isControlled = value !== undefined;
  const hasError = Boolean(error) && !readOnly;
  const showCounter = maxLength !== undefined && !readOnly && !hasError;
  const showFooter = !readOnly;
  const footerId = `${textareaId}-footer`;
  const describedBy =
    [ariaDescribedBy, hasError || showCounter ? footerId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  const accessibleName =
    label || ariaLabelledBy ? ariaLabel : (ariaLabel ?? "Text area");

  const valueForCount = isControlled ? getTextareaValue(value) : internalValue;

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    if (!isControlled) {
      setInternalValue(event.currentTarget.value);
    }

    onChange?.(event);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    onKeyDown?.(event);
    if (!event.defaultPrevented && event.key === "Escape") {
      event.currentTarget.blur();
    }
  }

  return (
    <Field.Root
      invalid={hasError}
      disabled={disabled}
      className="flex flex-col w-full"
    >
      {label && (
        <FieldLabel
          htmlFor={textareaId}
          label={label}
          required={required}
          tooltip={tooltip}
        />
      )}

      <div
        data-disabled={disabled || undefined}
        className={cn(
          fieldContainerVariants({ hasError }),
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <Field.Control
          render={
            <textarea
              {...props}
              id={textareaId}
              value={value}
              defaultValue={defaultValue}
              placeholder={readOnly ? undefined : placeholder}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              maxLength={maxLength}
              tabIndex={readOnly ? -1 : tabIndex}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              aria-label={accessibleName}
              aria-labelledby={ariaLabelledBy}
              aria-required={required || undefined}
              aria-invalid={hasError || undefined}
              aria-readonly={readOnly || undefined}
              aria-describedby={describedBy}
              className={textareaStyles()}
            />
          }
        />
      </div>

      {showFooter && (
        <div
          data-testid="textarea-footer"
          className="flex justify-between items-center min-h-(--spacing-20) mt-(--spacing-4) gap-(--spacing-8)"
        >
          <div className="flex-1 min-w-0">
            {hasError && (
              <Field.Error
                id={footerId}
                match
                className="truncate text-(length:--font-size-body-medium) leading-(--font-line-height-20) text-input-error-text"
              >
                {error}
              </Field.Error>
            )}
          </div>
          {showCounter && (
            <span
              id={footerId}
              aria-live="polite"
              className="shrink-0 text-(length:--font-size-body-small) leading-(--font-line-height-16) text-input-text-supporting whitespace-nowrap"
            >
              {valueForCount.length}/{maxLength}
            </span>
          )}
        </div>
      )}
    </Field.Root>
  );
}

export { Textarea };
